import { useEffect, useRef } from 'react';
import { useListContext } from 'react-admin';

import { suppressNextPresetSeed } from '@/util/DateFilters';

/**
 * Filter keys that, when set, should broaden the list to make the lookup
 * useful (an admin searching by name/email/phone almost never wants the
 * default "last 7 days, hide test accounts" cutoff in the way).
 */
const SPECIAL_FIELDS = [
  '_id',
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
] as const;

const DATE_FIELD = 'createdAt';
const DATE_PRESET_KEY = `${DATE_FIELD}DatePreset` as const;
const DATE_GTE_KEY = `${DATE_FIELD}Gte` as const;
const DATE_LTE_KEY = `${DATE_FIELD}Lte` as const;

const hasValue = (v: unknown): boolean =>
  v !== undefined &&
  v !== null &&
  v !== '' &&
  !(typeof v === 'string' && v.trim() === '');

const hasAnySpecialValue = (filters: Record<string, unknown>): boolean =>
  SPECIAL_FIELDS.some(f => hasValue(filters[f]));

const hasIdValue = (filters: Record<string, unknown>): boolean =>
  hasValue(filters._id);

const broadenDefaults = <T extends Record<string, unknown>>(
  filters: T,
): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...filters };
  next[DATE_PRESET_KEY] = 'custom';
  delete next[DATE_GTE_KEY];
  delete next[DATE_LTE_KEY];
  next.isHiddenTestAccount = 'visible';
  return next;
};

/**
 * When the admin filters by `_id`, every other filter is irrelevant — collapse
 * the filter set down to just the id plus permissive values for the alwaysOn
 * rows so the form stays valid and the query returns the matching therapist.
 */
const onlyIdFilters = (idValue: unknown): Record<string, unknown> => ({
  _id: idValue,
  [DATE_PRESET_KEY]: 'custom',
  isHiddenTestAccount: 'visible',
  deletedAt: 'visible',
  signupCompletedAt: 'visible',
});

/**
 * Watches filter transitions and adjusts the rest of the filter set so admins
 * can quickly look up therapists by id / name / email / phone without being
 * silently filtered out by the default `last 7 days` / `hidden test accounts`
 * filters. Rules:
 *  - id set    -> wipe every other filter (keep alwaysOn rows permissive).
 *  - any other special field set -> switch date preset to custom (clearing
 *    the gte/lte range) and flip test accounts to visible.
 *  - all special fields cleared -> restore the default date preset.
 */
export const useTherapistsFiltersAutoBroaden = () => {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const prevHadSpecialRef = useRef(false);
  const prevHadIdRef = useRef(false);

  useEffect(() => {
    const hasId = hasIdValue(filterValues);
    const hasSpecial = hasAnySpecialValue(filterValues);

    if (hasId && !prevHadIdRef.current) {
      suppressNextPresetSeed(DATE_FIELD);
      setFilters(onlyIdFilters(filterValues._id), displayedFilters, false);
      prevHadIdRef.current = true;
      prevHadSpecialRef.current = true;
      return;
    }

    if (hasSpecial && !prevHadSpecialRef.current) {
      suppressNextPresetSeed(DATE_FIELD);
      setFilters(broadenDefaults(filterValues), displayedFilters, false);
      prevHadSpecialRef.current = true;
      prevHadIdRef.current = hasId;
      return;
    }

    if (!hasSpecial && prevHadSpecialRef.current) {
      const restored: Record<string, unknown> = { ...filterValues };
      delete restored[DATE_PRESET_KEY];
      delete restored[DATE_GTE_KEY];
      delete restored[DATE_LTE_KEY];
      setFilters(restored, displayedFilters, false);
      prevHadSpecialRef.current = false;
      prevHadIdRef.current = false;
      return;
    }

    prevHadIdRef.current = hasId;
    prevHadSpecialRef.current = hasSpecial;
  }, [filterValues, displayedFilters, setFilters]);
};

/**
 * Side-effect-only component that hosts {@link useTherapistsFiltersAutoBroaden}
 * inside the List context. Renders nothing.
 */
export const TherapistsFiltersAutoBroadenController = () => {
  useTherapistsFiltersAutoBroaden();
  return null;
};
